import { notification } from 'antd';
import { db } from 'firebase/firebase';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import moment from 'moment';

import { getCollectionData } from '@src/packages/utils';
import TriggerRepository from '@src/repositories/TriggerRepository';
import { deleteTrigger } from '@src/util/firebase-operations/triggers/delete';
import safeExecute from '@src/util/safeExecute';

class TriggerService {
  constructor() {
    this.repository = new TriggerRepository();
  }

  /**
   * Parse trigger from raw data
   * @private
   */
  _parseTrigger(trigger) {
    return {
      id: trigger.id,
      name: trigger.name,
      status: trigger.status,
      locations: trigger.locations || [],
      eventType: trigger.eventType || null,
      expectedExecutions: trigger.expectedExecutions || 0,
      finishedExecutions: trigger.finishedExecutions || 0,
    };
  }
  /**
   * Delete trigger from Firestore
   * @param {string} orgId - Organization ID
   * @param {string} divId - Division ID
   * @param {string} triggerId - Trigger ID
   * @param {object} intl - Intl object for translated messages
   * @returns {Promise<void>}
   */
  async deleteTrigger({ orgId, divId, triggerId, intl }) {
    // First
    const queryRef = query(
      collection(db, 'organizations', orgId, 'divisions', divId, 'schedules'),
      where('schedule.endAt', '>', moment().subtract(1, 'day').toDate()),
      orderBy('schedule.endAt', 'desc'),
    );

    const docs = await getDocs(queryRef);
    const data = getCollectionData(docs);
    const todayDate = moment();
    const activeSchedules = [];

    data.forEach(element => {
      const executionDate = moment(element?.schedule?.startAt.toDate());
      const endDate = moment(element?.schedule?.endAt.toDate());

      if (executionDate.isBefore(todayDate) && endDate.isAfter(todayDate)) {
        activeSchedules.push(element);
      }
    });

    // Second
    const activeTriggerIds = activeSchedules.reduce((acc, val) => {
      let triggerIds = val?.triggers.map(item => item?.triggerId);
      triggerIds = [...acc, ...triggerIds];
      return [...new Set(triggerIds)];
    }, []);

    const hasSomeOne = activeTriggerIds.includes(triggerId);
    if (hasSomeOne) {
      return notification.warning({
        message: intl.formatMessage({ id: 'triggers.list.delete.activeTrigger.message' }),
        placement: 'topRight',
      });
    } else {
      return safeExecute(
        async () => await deleteTrigger({ orgId, divId, triggerId: triggerId }),
      ).then(() => {
        notification.success({
          message: intl.formatMessage({ id: 'general.save.successful.message' }),
          placement: 'topRight',
        });
      });
    }
  }

  /**
   * Get all triggers from an execution
   * @param {string} organizationId - Organization ID
   * @param {string} divisionId - Division ID
   * @param {string} executionId - Execution ID
   * @returns {Promise<Array<Object>>} Array of parsed triggers
   */
  async getTriggersByExecution(organizationId, divisionId, executionId) {
    const triggers = await this.repository.getTriggersByExecution(
      organizationId,
      divisionId,
      executionId,
    );
    return triggers.map(trigger => this._parseTrigger(trigger));
  }
}

export default TriggerService;
